import { z } from 'zod';

/**
 * Best-Of Category Configuration
 * Defines SEO-optimized category pages (e.g., "Best CRM for Real Estate")
 */
export const ZBestOfCategory = z.object({
  slug: z.string(),
  title: z.string(),
  metaTitle: z.string(),
  metaDescription: z.string(),
  h1: z.string(),
  introText: z.string(),
  categoryFilter: z.string(), // Maps to products.categories array value
  serviceType: z.string().optional(), // Maps to products.service_type
  faqItems: z.array(z.object({
    question: z.string(),
    answer: z.string(),
  })),
});

export type BestOfCategory = z.infer<typeof ZBestOfCategory>;

/**
 * Hardcoded category configs (expand as needed)
 */
export const BEST_OF_CATEGORIES: Record<string, BestOfCategory> = {
  'crm': {
    slug: 'crm',
    title: 'Best CRM for Real Estate Agents',
    metaTitle: 'Best CRM for Real Estate Agents 2025 | Circle',
    metaDescription: 'Compare the top CRM systems for real estate agents. Circle Score rankings, pricing, and expert reviews.',
    h1: 'Best CRM for Real Estate Agents (2025)',
    introText: 'These are the highest-rated CRM systems for real estate agents, ranked by our Circle Score algorithm that weighs popularity, value, ease of use, support quality, and ROI.',
    categoryFilter: 'CRM',
    serviceType: 'software',
    faqItems: [
      { question: 'What is the best CRM for real estate agents?', answer: 'The best CRM depends on your business size and budget. Top-rated options include KvCore, Follow Up Boss, and LionDesk.' },
      { question: 'How much does a real estate CRM cost?', answer: 'CRM pricing ranges from $25/month for basic plans to $500+/month for enterprise solutions with advanced automation.' },
      { question: 'Do I need a CRM as a new agent?', answer: 'Yes! A CRM helps you stay organized, follow up with leads consistently, and build long-term client relationships from day one.' },
    ],
  },
  'lead-generation': {
    slug: 'lead-generation',
    title: 'Best Lead Generation Tools for Real Estate',
    metaTitle: 'Best Lead Generation Tools for Real Estate 2025 | Circle',
    metaDescription: 'Top lead generation platforms ranked by Circle Score. Compare pricing, features, and ROI for real estate agents.',
    h1: 'Best Lead Generation Tools for Real Estate (2025)',
    introText: 'Generate more seller and buyer leads with these top-rated platforms, ranked by effectiveness, cost per lead, and ease of use.',
    categoryFilter: 'Lead Generation',
    serviceType: 'software',
    faqItems: [
      { question: 'What is the best lead generation tool for real estate?', answer: 'Popular options include Zillow Premier Agent, Opcity, and BoldTrail. The best choice depends on your market and budget.' },
      { question: 'How much do real estate leads cost?', answer: 'Lead costs vary from $20-$200+ per lead depending on market competitiveness and lead quality.' },
      { question: 'Are paid leads worth it?', answer: 'Paid leads can be profitable if you have strong follow-up systems and convert at 2-5%. Track your cost per closed deal.' },
    ],
  },
  'transaction-management': {
    slug: 'transaction-management',
    title: 'Best Transaction Management Software for Agents',
    metaTitle: 'Best Transaction Management Software 2025 | Circle',
    metaDescription: 'Compare top transaction management platforms. Circle Score rankings, pricing, and compliance features.',
    h1: 'Best Transaction Management Software (2025)',
    introText: 'Stay organized and compliant with these top transaction management systems, ranked by ease of use, compliance features, and integration capabilities.',
    categoryFilter: 'Transaction Management',
    serviceType: 'software',
    faqItems: [
      { question: 'What is transaction management software?', answer: 'Transaction management software helps agents track deadlines, manage documents, and ensure compliance throughout the closing process.' },
      { question: 'Do I need transaction management software?', answer: 'If you handle multiple transactions simultaneously, transaction management software prevents missed deadlines and keeps all parties informed.' },
      { question: 'What features should I look for?', answer: 'Key features include document storage, deadline tracking, e-signature integration, compliance checklists, and team collaboration.' },
    ],
  },
};

export const getBestOfCategoryBySlug = (slug: string): BestOfCategory | null => {
  return BEST_OF_CATEGORIES[slug] || null;
};

export const getAllBestOfSlugs = (): string[] => {
  return Object.keys(BEST_OF_CATEGORIES);
};
